import React, { useState, useMemo } from "react";
import { Maximize2, ArrowRight, ChevronRight, ClipboardList } from "lucide-react";
import { services } from "../mock/mock";
import { useLightbox } from "../context/LightboxContext";

const tabs = [
  { id: "all", label: "All Services" },
  { id: "delivery", label: "delivery" },
  { id: "hauling", label: "hauling" },
  { id: "debris", label: "debris" },
];

export default function Services() {
  const [active, setActive] = useState("all");
  const { openLightbox } = useLightbox();

  const visible = useMemo(
    () =>
      active === "all" ? services : services.filter((s) => s.tag === active),
    [active]
  );

  const lightboxItems = visible.map((s) => ({
    image: s.image,
    title: s.title,
    caption: s.description,
    rotate: s.rotate,
  }));

  return (
    <section id="services" className="border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="section-label mb-4">What We Do Best</div>
            <h2 className="section-heading text-[30px] sm:text-[36px] md:text-[46px] leading-[1.05]">
              Professional Hauling Services
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[#b6b3ab] max-w-xl">
              From residential driveways to massive commercial site works.
              Ortiz Truck &amp; Landscaping handles aggregate deliveries and
              complex debris removal efficiently.
            </p>
          </div>

          <div className="inline-flex items-center gap-1 chip rounded-lg p-1 overflow-x-auto max-w-full">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`text-[11.5px] font-semibold tracking-[0.14em] uppercase px-3 py-2 rounded-md transition-colors whitespace-nowrap shrink-0 ${
                  active === t.id
                    ? "bg-[#f4efe6] text-[#0a0a0b]"
                    : "text-[#a9a69d] hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((s, idx) => (
            <article
              key={s.id}
              className="card-outline rounded-xl overflow-hidden hover-lift group"
            >
              <button
                type="button"
                onClick={() => openLightbox(lightboxItems, idx)}
                className="relative aspect-[16/10] overflow-hidden block w-full text-left"
              >
                <img
                  src={s.image}
                  alt={s.title}
                  style={
                    s.rotate
                      ? { transform: `rotate(${s.rotate}deg) scale(1.9)` }
                      : undefined
                  }
                  className="photo-crisp w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 img-overlay" />
                <div className="absolute top-3 right-3 chip bg-[#0a0a0b]/70 backdrop-blur rounded-md px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] text-[#c8c5bc]">
                  <Maximize2 size={12} />
                  Full Photo
                </div>
              </button>
              <div className="p-6">
                <div className="inline-flex items-center gap-1.5 chip rounded-full px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#c9b98a]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#c9b98a]" />
                  {s.tag}
                </div>
                <h3 className="mt-3 font-display font-bold text-[22px] text-[#f4efe6]">
                  {s.title}
                </h3>
                <p className="mt-2 text-[13.5px] text-[#b6b3ab] leading-relaxed">
                  {s.description}
                </p>

                <div className="mt-5 pt-5 border-t divider-soft">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8a877f] font-semibold mb-3">
                    Included Materials / Capabilities:
                  </div>
                  <ul className="space-y-2">
                    {s.capabilities.map((c) => (
                      <li
                        key={c}
                        className="flex items-center gap-2 text-[13px] text-[#c8c5bc]"
                      >
                        <ChevronRight
                          size={14}
                          className="text-[#c9b98a] shrink-0"
                        />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Custom aggregate CTA */}
        <div className="mt-10 card-outline rounded-xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg chip flex items-center justify-center">
              <ClipboardList size={20} className="text-[#c9b98a]" />
            </div>
            <div>
              <h4 className="font-display font-bold text-[18px] text-[#f4efe6]">
                Looking for bespoke hauling or custom aggregate?
              </h4>
              <p className="text-[13px] text-[#8a877f] mt-1">
                Tell us your project needs and we&apos;ll dispatch a custom
                quote fast.
              </p>
            </div>
          </div>
          <a
            href="#contact"
            className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-md text-[13px] font-semibold whitespace-nowrap"
          >
            Ask for Custom Material
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
